/**
 * ParentBridgeContext — typed wrapper around postMessage to/from the parent
 * window. The bootstrap script forwards `wg:identify`, `wg:nav`, and
 * `wg:page_model` here; we expose `send` so views can emit `wg:event`
 * back up (analytics, completed-booking notifications, etc.) plus typed
 * helpers for the page-context bridge: `setCapabilities`, `requestPageModel`,
 * `dispatchAction`.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { auth } from '../client';
import logger from '../utils/logger';

interface ParentMessage {
  type: string;
  payload?: unknown;
}

export interface ParentLocation {
  url: string;
  origin: string;
  path: string;
  search: string;
  hash: string;
  title: string | null;
  referrer: string | null;
}

export interface ParentPageElement {
  id: string;
  kind: string;
  label?: string;
  href?: string;
  href_kind?: string;
  region?: string;
  in_viewport?: boolean;
  meta?: Record<string, unknown>;
}

export interface ParentPageRegion {
  id: string;
  selector?: string;
  label?: string;
  element_count?: number;
}

export interface ParentPageModel {
  mode: 'full' | 'delta';
  version_n: number;
  page: {
    url_path: string;
    url_origin: string;
    title: string | null;
    lang: string | null;
  };
  summary: {
    main_text_excerpt?: string;
    section_titles?: string[];
  };
  regions: Record<string, ParentPageRegion>;
  elements: Record<string, ParentPageElement>;
  removed_ids?: string[];
}

export type PageActionVerb =
  | 'highlight'
  | 'scroll_into_view'
  | 'focus'
  | 'click'
  | 'set_value'
  | 'navigate'
  | 'copy_to_clipboard'
  | 'emit_event';

export interface PageActionResult {
  ok: boolean;
  error?: string;
  detail?: unknown;
}

export interface ParentCapabilities {
  enabled: boolean;
  routes?: { include?: string[]; exclude?: string[] };
  regions?: {
    include_selectors?: string[];
    exclude_selectors?: string[];
  };
  redaction?: { patterns?: string[] };
  actions?: {
    enabled?: boolean;
    denied?: PageActionVerb[];
    auto_grant?: Array<{ verb: PageActionVerb; scope_selector: string }>;
  };
  limits?: {
    max_elements?: number;
    max_regions?: number;
    debounce_ms?: number;
  };
}

/** Runtime layout state mirrored from the bootstrap container. */
export type LayoutMode = 'native' | 'expanded' | 'maximized';

interface ParentBridgeValue {
  send: (msg: ParentMessage) => void;
  requestClose: () => void;
  /** Open the panel from inside the iframe (e.g. auth-gated host action). */
  requestOpen: () => void;
  /** Latest known parent-page URL (updates as the host SPA navigates). */
  parentLocation: ParentLocation | null;
  /** Latest page model relayed by the bootstrap. */
  pageModel: ParentPageModel | null;
  /** Forward partner capabilities to the bootstrap dispatcher. */
  setCapabilities: (caps: ParentCapabilities) => void;
  /** Ask the bootstrap to send a fresh `mode === 'full'` snapshot. */
  requestPageModel: () => void;
  /**
   * Dispatch a page action. Returns when the bootstrap acknowledges.
   * Times out after 5s with `{ ok:false, error:'timeout' }`.
   */
  dispatchAction: (
    verb: PageActionVerb,
    args: Record<string, unknown>,
  ) => Promise<PageActionResult>;
  /**
   * Ask the bootstrap to swap the panel container layout. Bootstrap caps
   * the request against `theme.panel.layoutMax` and replies with
   * `wg:layout_changed`, which updates `layoutMode` / `layoutMax` below.
   */
  requestLayout: (mode: LayoutMode) => void;
  /** Currently-applied layout (echoed by bootstrap). */
  layoutMode: LayoutMode;
  /**
   * Cap reported by the bootstrap (derived from theme.panel.layoutMax,
   * defaults to 'expanded'). Drives whether the header toggle renders
   * and how far auto-expand is allowed to push.
   */
  layoutMax: LayoutMode;
  /**
   * Host (parent window) viewport, relayed by the bootstrap on ready and
   * on resize. Used for chrome-only decisions (e.g. hiding the expand
   * toggle on phones) since `window.innerWidth` inside the iframe
   * reports the iframe width, not the host viewport.
   */
  hostViewport: { width: number; isMobile: boolean } | null;
}

const ParentBridgeContext = createContext<ParentBridgeValue | undefined>(
  undefined,
);

const PARENT_ORIGIN_FALLBACK = '*';
const ACTION_TIMEOUT_MS = 5000;

function parentOriginFromUrl(): string {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('origin') || PARENT_ORIGIN_FALLBACK;
  } catch {
    return PARENT_ORIGIN_FALLBACK;
  }
}

function newActionId(): string {
  return `act_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

export const ParentBridgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const targetOriginRef = useRef<string>(parentOriginFromUrl());
  const [parentLocation, setParentLocation] = useState<ParentLocation | null>(
    null,
  );
  const [pageModel, setPageModel] = useState<ParentPageModel | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('native');
  // Cap defaults to 'expanded' (default-allow). Bootstrap echoes the real
  // value via wg:layout_changed once theme resolves.
  const [layoutMax, setLayoutMax] = useState<LayoutMode>('expanded');
  const [hostViewport, setHostViewport] = useState<{
    width: number;
    isMobile: boolean;
  } | null>(null);
  const pendingActionsRef = useRef<
    Map<
      string,
      {
        resolve: (r: PageActionResult) => void;
        timer: ReturnType<typeof setTimeout>;
      }
    >
  >(new Map());

  const send = useCallback((msg: ParentMessage) => {
    try {
      window.parent.postMessage(msg, targetOriginRef.current);
    } catch (err) {
      logger.warn('[embed] parent postMessage failed', err);
    }
  }, []);

  const requestClose = useCallback(() => {
    send({ type: 'wg:request_close' });
  }, [send]);

  const requestOpen = useCallback(() => {
    send({ type: 'wg:request_open' });
  }, [send]);

  const setCapabilities = useCallback(
    (caps: ParentCapabilities) => {
      send({ type: 'wg:capabilities', payload: caps });
    },
    [send],
  );

  const requestPageModel = useCallback(() => {
    send({ type: 'wg:request_page_model' });
  }, [send]);

  const dispatchAction = useCallback(
    (verb: PageActionVerb, args: Record<string, unknown>) =>
      new Promise<PageActionResult>(resolve => {
        const id = newActionId();
        const timer = setTimeout(() => {
          if (pendingActionsRef.current.delete(id)) {
            resolve({ ok: false, error: 'timeout' });
          }
        }, ACTION_TIMEOUT_MS);
        pendingActionsRef.current.set(id, { resolve, timer });
        send({ type: 'wg:action', payload: { id, verb, args } });
      }),
    [send],
  );

  const requestLayout = useCallback(
    (mode: LayoutMode) => {
      send({ type: 'wg:request_layout', payload: { mode } });
    },
    [send],
  );

  // Parent → iframe message dispatcher.
  useEffect(() => {
    const handler = async (ev: MessageEvent) => {
      if (ev.source !== window.parent) return;
      const data = ev.data as ParentMessage | undefined;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string')
        return;
      if (data.type === 'wg:identify') {
        const payload = data.payload as
          | {
              accessToken?: string;
              refreshToken?: string;
              expiresAt?: number;
            }
          | undefined;
        if (payload?.accessToken && payload.refreshToken && payload.expiresAt) {
          try {
            await auth.setSessionFromExternal({
              accessToken: payload.accessToken,
              refreshToken: payload.refreshToken,
              expiresAt: payload.expiresAt,
            });
          } catch (err) {
            logger.error('[embed] setSessionFromExternal failed', err);
          }
        }
      } else if (data.type === 'wg:nav') {
        const p = data.payload as Partial<ParentLocation> | undefined;
        if (p && typeof p.url === 'string') {
          setParentLocation({
            url: p.url,
            origin: p.origin ?? '',
            path: p.path ?? '',
            search: p.search ?? '',
            hash: p.hash ?? '',
            title: p.title ?? null,
            referrer: p.referrer ?? null,
          });
        }
      } else if (data.type === 'wg:page_model') {
        const p = data.payload as ParentPageModel | undefined;
        if (p && typeof p.version_n === 'number') {
          setPageModel(p);
        }
      } else if (data.type === 'wg:layout_changed') {
        const p = data.payload as
          | { mode?: LayoutMode; max?: LayoutMode }
          | undefined;
        if (
          p?.mode === 'native' ||
          p?.mode === 'expanded' ||
          p?.mode === 'maximized'
        ) {
          setLayoutMode(p.mode);
        }
        if (
          p?.max === 'native' ||
          p?.max === 'expanded' ||
          p?.max === 'maximized'
        ) {
          setLayoutMax(p.max);
        }
      } else if (data.type === 'wg:host_viewport') {
        const p = data.payload as
          | { width?: number; isMobile?: boolean }
          | undefined;
        if (
          p &&
          typeof p.width === 'number' &&
          typeof p.isMobile === 'boolean'
        ) {
          setHostViewport({ width: p.width, isMobile: p.isMobile });
        }
      } else if (data.type === 'wg:action_result') {
        const p = data.payload as
          | { id?: string; ok?: boolean; error?: string; detail?: unknown }
          | undefined;
        const id = p?.id;
        if (typeof id === 'string') {
          const pending = pendingActionsRef.current.get(id);
          if (pending) {
            clearTimeout(pending.timer);
            pendingActionsRef.current.delete(id);
            pending.resolve({
              ok: !!p?.ok,
              error: p?.error,
              detail: p?.detail,
            });
          }
        }
      }
    };
    window.addEventListener('message', handler);
    // Tell parent we're alive so any pending identify gets flushed.
    try {
      window.parent.postMessage({ type: 'wg:ready' }, targetOriginRef.current);
    } catch {
      /* noop */
    }
    return () => {
      window.removeEventListener('message', handler);
    };
  }, []);

  return (
    <ParentBridgeContext.Provider
      value={{
        send,
        requestClose,
        requestOpen,
        parentLocation,
        pageModel,
        setCapabilities,
        requestPageModel,
        dispatchAction,
        requestLayout,
        layoutMode,
        layoutMax,
        hostViewport,
      }}
    >
      {children}
    </ParentBridgeContext.Provider>
  );
};

export function useParentBridge(): ParentBridgeValue {
  const ctx = useContext(ParentBridgeContext);
  if (!ctx)
    throw new Error('useParentBridge must be used within ParentBridgeProvider');
  return ctx;
}
